import { useTranslation } from 'react-i18next';
import DetailsHeader from '../../siteDetails/header/DetailsHeader';
import ProductDetailsList, { ProductDetailsSection } from './ProductDetailsList';
import { useWaProductStore } from 'src/api/wa/store/WaProductStore';
import LinkButton from 'src/ui/wards/common/button/LinkButton';
import { wardsNavigate } from 'src/ui/wards/router/WardsScreenRegistration';
import { useNavigate } from 'react-router-dom';
import { WA_Screen } from 'src/ui/wards/router/WardsScreens';
import { WaScreensMap } from 'src/ui/wards/router/WardsScreenMap';
import { getDetailStatusTranslationKey } from 'src/api/wa/store/models/workplace/OperationStatus';
import { formatJpnDate } from 'src/common/formatJpnDate/formatJpnDate';
import { useEffect } from 'react';
import useWaProductService from 'src/api/wa/service/WaProductService';
import { useWaWorkplaceStore } from 'src/api/wa/store/WaWorkplaceStore';

const toDisplayText = (value?: string | null) => {
  if (value === null || value === undefined) return '';

  // Filter out 'null' strings from comma-separated values
  const trimmed = value.trim();
  if (trimmed === '' || trimmed === 'null') return '';

  // Handle comma-separated values with potential nulls (e.g., "null, null, null")
  if (trimmed.includes(',')) {
    const filtered = trimmed
      .split(',')
      .map((s) => s.trim())
      .filter((s) => s && s !== 'null')
      .join(', ');
    return filtered;
  }

  return trimmed;
};

const S4_3_ProductDetails = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { selectedProduct, setSelectedProduct, productDetails } = useWaProductStore();
  const { fetchProductDetails, fetchProductMasterDetails } = useWaProductService();
  const { selectedWorkplace } = useWaWorkplaceStore();

  useEffect(() => {
    if (selectedProduct && selectedWorkplace) {
      if (selectedProduct.disasterId) {
        fetchProductDetails(selectedProduct, selectedWorkplace);
      } else {
        fetchProductMasterDetails(selectedProduct, selectedWorkplace);
      }
    }
  }, [fetchProductDetails, fetchProductMasterDetails, selectedProduct, selectedWorkplace]);

  const workHistorySections: ProductDetailsSection[] = (productDetails?.workRecords ?? [])
    .slice(0, 3)
    .map((record, index) => ({
      key: `workHistory${index + 1}`,
      title: t(`wards.productDetails.history.title${index + 1}`),
      items: [
        {
          placeholder: t('wards.productDetails.history.workerId'),
          value: toDisplayText(record.kojUserId),
        },
        {
          placeholder: t('wards.productDetails.history.workerName'),
          value: toDisplayText(record.kojUserName),
        },
        {
          placeholder: t('wards.productDetails.history.jobName'),
          value: toDisplayText(record.sgyoName),
        },
        {
          placeholder: t('wards.productDetails.history.jobDate'),
          value: toDisplayText(
            record.sgyoDate ? formatJpnDate({ iso: record.sgyoDate }).split(' ')[0] : null
          ),
        },
        {
          placeholder: t('wards.productDetails.history.jobContent'),
          value: toDisplayText(record.sgyoComment),
        },
      ],
    }));

  const sections: ProductDetailsSection[] = [
    {
      key: 'productInformation',
      title: t('wards.productDetails.product.title'),
      items: [
        {
          placeholder: t('wards.productDetails.product.productNumber'),
          value: toDisplayText(selectedProduct?.productNumber),
        },
        {
          placeholder: t('wards.productDetails.product.machineNumber'),
          value: toDisplayText(selectedProduct?.unit),
        },
        {
          placeholder: t('wards.productDetails.product.contract'),
          value: toDisplayText(selectedProduct?.contractName),
        },
        {
          placeholder: t('wards.productDetails.product.type'),
          value: toDisplayText(selectedProduct?.model),
        },
        {
          placeholder: t('wards.productDetails.product.organizationCode'),
          value: toDisplayText(productDetails?.organizationCode),
        },
        {
          placeholder: t('wards.productDetails.product.branchCode'),
          value: toDisplayText(productDetails?.branchCode),
        },
        {
          placeholder: t('wards.productDetails.product.salesOfficeCode'),
          value: toDisplayText(productDetails?.salesOfficeCode),
        },
        {
          placeholder: t('wards.productDetails.product.sectionCode'),
          value: toDisplayText(productDetails?.sectionCode),
        },
        {
          placeholder: t('wards.productDetails.product.squadCode'),
          value: toDisplayText(productDetails?.squadCode),
        },
        {
          placeholder: t('wards.productDetails.product.priorityPoints'),
          value: toDisplayText(productDetails?.priorityPoints),
        },
        {
          placeholder: t('wards.productDetails.product.safetyPoints'),
          value: toDisplayText(productDetails?.safetyPoints),
        },
      ],
    },
    ...workHistorySections,
    {
      key: 'wideAreaDisasterInformation',
      title: t('wards.productDetails.wideArea.title'),
      items: [
        {
          placeholder: t('wards.productDetails.wideArea.disasterId'),
          value: toDisplayText(productDetails?.disasterId),
        },
        {
          placeholder: t('wards.productDetails.wideArea.state'),
          value: toDisplayText(
            productDetails?.statusId
              ? t(getDetailStatusTranslationKey(productDetails?.statusId))
              : null
          ),
        },
        {
          placeholder: t('wards.productDetails.wideArea.handler'),
          value: toDisplayText(productDetails?.addressedName),
        },
        {
          placeholder: t('wards.productDetails.wideArea.handlingHours'),
          value: toDisplayText(
            productDetails?.addressedOn ? formatJpnDate({ iso: productDetails?.addressedOn }) : null
          ),
        },
        {
          placeholder: t('wards.productDetails.wideArea.lastUpdate'),
          value: toDisplayText(
            productDetails?.lastUpdateDate
              ? formatJpnDate({ iso: productDetails?.lastUpdateDate })
              : null
          ),
        },
        {
          placeholder: t('wards.productDetails.wideArea.operationId'),
          value: (
            <LinkButton
              title={selectedWorkplace?.name || ''}
              onClick={() => {
                setSelectedProduct(selectedProduct);
                wardsNavigate(navigate, WaScreensMap[WA_Screen.WorkplaceDetails].path);
              }}
            />
          ),
        },
      ],
    },
  ];

  return (
    <div className="min-h-screen bg-gray-100 pt-4">
      <DetailsHeader
        siteName={t('wards.productDetails.title', { productId: selectedProduct?.productNumber })}
      />
      <ProductDetailsList sections={sections} />
    </div>
  );
};

export default S4_3_ProductDetails;
